/**
 * The default locale to use when no valid locale can be extracted or detected.
 * Should be a valid BCP 47 language tag (e.g., "en" for English).
 * Used as a fallback throughout the localization logic.
 *
 * @type {string}
 */
const fallbackLocale = 'en';

/**
 * Extracts the language and region subtags from a BCP 47 locale string.
 *
 * Examples:
 *   "en-GB-oxendict"   => "en-GB"
 *   "zh-Hans-CN"       => "zh-CN"
 *   "fr"               => "fr"
 *   "es-419"           => "es-419"
 *
 * If the region subtag is not present, returns only the language subtag.
 * If the input is not a valid locale string, returns the fallbackLocale
 *
 * @param {string} locale - A BCP 47 locale string (e.g., "en-GB-oxendict", "zh-Hans-CN", "fr").
 * @returns {string|undefined} The extracted language-region string (e.g., "en-GB"), just the language (e.g., "fr"), or undefined if input is empty.
 */
function extractLanguageAndRegion(locale) {
  const match = locale && locale.match(/^([a-zA-Z]{2,3})(?:-[a-zA-Z]{4})?(?:-([a-zA-Z]{2}|\d{3}))?/);
  if (!match) return fallbackLocale;
  return match[2] ? `${match[1]}-${match[2]}` : match[1];
}

/**
 * Detects the user's preferred locale, returning a language or language-region code.
 *
 * The function attempts to determine the user's locale in this order:
 * 1. The first entry in `navigator.languages` (preferred browser languages).
 * 2. `navigator.language` (browser UI language).
 * 3. The locale from `Intl.DateTimeFormat().resolvedOptions().locale` (OS or environment locale).
 * 4. Falls back to `fallbackLocale` if none are available.
 *
 * The returned value is normalized to include only the language or language-region (e.g., "en", "en-GB").
 *
 * @returns {string} The user's preferred locale (e.g., "en", "fr-CA", "zh-CN"), or the fallback locale.
 */
export const getUserLocale = () => {
  if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
    return extractLanguageAndRegion(navigator.languages[0]);
  }

  if (navigator.language) {
    return extractLanguageAndRegion(navigator.language);
  }

  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    const osLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    if (osLocale) {
      return extractLanguageAndRegion(osLocale);
    }
  }

  return fallbackLocale;
};

/**
 * The user's preferred locale, as determined by {@link getUserLocale}.
 *
 * This will be a normalized BCP 47 language or language-region code
 * (e.g., "en", "en-GB", "fr-CA"), or the fallback locale if detection fails.
 *
 * @type {string}
 */
export const userLocale = getUserLocale();
